/**
 * The directory-lookup half of an offering parameter, read safely.
 *
 * Ivanti models "User name" as an ordinary pick list, and the connector can
 * only afford to page in the first couple of hundred rows — which is why the
 * dropdown showed 201 GUID-labelled entries out of 1,663 employees and most
 * colleagues were simply unreachable. The backend now marks those parameters
 * instead of enumerating them, and the browser searches the directory live.
 *
 * Three fields carry that:
 *
 *   - `lookup` — `"EMPLOYEE"` means "render the directory picker, ignore
 *     `options`". Null (or absent) leaves the parameter exactly as it was.
 *   - `default_display_value` — the human name behind `default_value`, so a
 *     field that defaults to the requester shows "Carl Wood" and not a RecId.
 *   - `manager_source_parameter_id` — set on a *manager* field, naming the
 *     employee-lookup parameter whose selection should fill it in.
 *
 * ## Why these are read through accessors
 *
 * All three are optional on the generated model and all three are `null` for
 * every parameter that is not a lookup — and two of them are strings that
 * Ivanti can return empty rather than absent. Funnelling the reads through
 * three functions means each caller gets one answer ("is it?", "what is it?")
 * instead of repeating the same `== null || === ""` dance and getting it
 * subtly wrong in one of the four places it is needed.
 */

import type { OfferingParameter } from "../api/contract";

/** True when this parameter should be answered from the employee directory. */
export function isEmployeeLookup(parameter: OfferingParameter): boolean {
  return parameter.lookup === "EMPLOYEE";
}

/**
 * The name to show for a lookup parameter's default value.
 *
 * Null when the offering declares no default, or declares one it cannot name —
 * in which case the picker falls back to showing the raw id rather than an
 * empty box, so at least the value is visible and correctable.
 */
export function defaultDisplayValue(parameter: OfferingParameter): string | null {
  const value = parameter.default_display_value;
  return value == null || value === "" ? null : value;
}

/**
 * The employee-lookup parameter whose selection fills this field, if any.
 *
 * Only manager fields carry it, and only when the offering pairs them with a
 * user field.
 */
export function managerSourceParameterId(parameter: OfferingParameter): string | null {
  const value = parameter.manager_source_parameter_id;
  return value == null || value === "" ? null : value;
}
